import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';

import { useTranslation } from 'react-i18next';

// ui
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
// import Button from '@material-ui/core/Button';

// leaflet
import L from 'leaflet';
import {
  Map, Marker, Popup, Tooltip, LayerGroup, LayersControl, TileLayer,
} from 'react-leaflet';
import Control from 'react-leaflet-control';
import FullscreenControl from 'react-leaflet-fullscreen';
import { GestureHandling } from 'leaflet-gesture-handling';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faSquare } from '@fortawesome/free-solid-svg-icons';
import LeafletFullScreenFix from '../../../utils/map/LeafletFullScreenFix';
import {
  Dam1Icon,
  Dam2Icon,
  Dam3Icon,
  Dam4Icon,
  Dam5Icon,
} from '../../../assets/svg/Icon';
import { FaChartLine } from '../../../assets/font/Fontawesome';

// components
import mapConfig from '../../../components/map/Config';
import DamLargeColor from '../../../components/color/dam/DamLargeColor';
import DamMediumColor from '../../../components/color/dam/DamMediumColor';
import DamModal from '../../../components/modal/Modal';
import DamChartDailyMain from '../chart/daily/DamChartDailyMain';
import DamChartDailyMediumMain from '../chart/daily/DamChartDailyMediumMain';

// layers
import LayerProvince from '../../../components/map/Province';
import LayerBasin from '../../../components/map/Basin';
import LayerRiver from '../../../components/map/River';
import WmsBoundary from '../../../components/map/WmsBoundary';
import WmsBasin from '../../../components/map/WmsBasin';
// import GroupWmsInfra from '../../../components/map/GroupWmsInfra';
// import baseMap from '../../../components/map/BaseMap';
// import LayerBase from '../../../components/map/BaseMapReact';

// style
import 'leaflet/dist/leaflet.css';
import '../../../components/map/Style.css';
import styles from './style';

const useStyles = makeStyles(styles);

const { Overlay } = LayersControl;

export default function DamMap(props) {
  const damIdNoTransaction = [231, 319, 320];
  const { t } = useTranslation();
  const classes = useStyles();

  const refMap = useRef(null);
  // const refProvince = useRef(null);
  // const refBasin = useRef(null);
  const refDamLargeLayer = useRef(null);
  const refDamMediumLayer = useRef(null);
  const [modalData, setModalData] = useState({
    open: false,
    header: 'กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน',
    subheader: null,
    content: null,
  });

  const {
    datasLargeDam, datasMediumDam, handleSetMap, currentMode, currentArea,
  } = props;
  // const [bounds] = useState(L.latLngBounds(currentArea.top_left, currentArea.bottom_right));

  useEffect(() => {
    // set up map reference to list component
    handleSetMap(refMap.current.leafletElement);

    // set bounds
    refMap.current.leafletElement.fitBounds(
      L.latLngBounds(currentArea.top_left, currentArea.bottom_right), { padding: [50, 50] },
    );

    setModalData({
      open: false,
      header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
      subheader: null,
      content: null,
    });

    // add map gesture zoom
    L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSetMap, currentMode, currentArea]);

  // add marker reference
  const bindMarker = (id) => (ref) => {
    const { markers } = props;

    if (ref) {
      markers[id] = ref.leafletElement;
    }
  };

  const handleChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyMain id={data} agencyId={12} />,
    });
  };

  const handleChartMedium = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟอ่างเก็บน้ำขนาดกลางรายวัน กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyMediumMain id={data} agencyId={12} />,
    });
  };

  // set marker icon based on criteria
  const setIcon = (val, type) => {
    let status = null;

    if (type === 'large') {
      status = DamLargeColor(val);
    } else {
      status = DamMediumColor(val);
    }

    return status.icon;
  };

  const setHighlightIcon = (val) => {
    const status = DamLargeColor(val);
    let icon = '';
    // setup icon
    if (val > 80) {
      icon = <Dam5Icon style={{ fontSize: '5rem', fill: status.color.statusColor }} />;
    } else if (val > 60) {
      icon = <Dam4Icon style={{ fontSize: '5rem', fill: status.color.statusColor }} />;
    } else if (val > 40) {
      icon = <Dam3Icon style={{ fontSize: '5rem', fill: status.color.statusColor }} />;
    } else if (val > 20) {
      icon = <Dam2Icon style={{ fontSize: '5rem', fill: status.color.statusColor }} />;
    } else if (val > 0) {
      icon = <Dam1Icon style={{ fontSize: '5rem', fill: status.color.statusColor }} />;
    } else {
      icon = <Dam1Icon style={{ fontSize: '5rem', fill: status.color.statusColor }} />;
    }

    return icon;
  };

  const eachLargeDamData = (data, i) => {
    if (data.dam.dam_lat && data.dam.dam_long) {
      // marker position
      const position = [parseFloat(data.dam.dam_lat), parseFloat(data.dam.dam_long)];

      // marker icons
      const icon = setIcon(parseFloat(data.dam_storage_percent).toFixed(2), 'large');

      // highlight icon in popup
      const highlightIcon = setHighlightIcon(parseFloat(data.dam_storage_percent).toFixed(2));
      const color = DamLargeColor(parseFloat(data.dam_storage_percent).toFixed(2));

      return (
        <Marker
          id={data.id + data.geocode.id}
          key={i}
          position={position}
          icon={icon}
          ref={bindMarker(data.id + data.geocode.id)}
        >
          <Popup autoPan>
            <div>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={2}>
                      <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                        {`${t('อ่างเก็บน้ำ')}${data.dam.dam_name.th} `}
                      </Box>
                      <Box fontFamily="Kanit" fontSize="12px">
                        {`${t('จ.')}${data.geocode.province_name.th}`}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {!damIdNoTransaction.includes(data.dam.id)
                    && (
                      <>
                        <TableRow>
                          <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={2}>
                            <Box display="flex" justifyContent="center" alignItems="center">
                              <Box>{highlightIcon}</Box>
                              <Box className={classes.statusValue}>
                                <span style={{ color: color.color.statusColor }}>
                                  {`${parseFloat(data.dam_storage_percent).toFixed(0)}%`}
                                </span>
                              </Box>
                            </Box>
                            <Box mb={2} fontSize="16px" fontFamily="Kanit">
                              {t('ปริมาณน้ำกักเก็บปัจจุบัน (% รนก.)')}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell padding="none" align="center" colSpan={3} />
                        </TableRow>
                        <TableRow>
                          <TableCell
                            className={classes.hiddenLine}
                          >
                            <Box fontWeight={600} fontSize={12}>{t('ปริมาณน้ำกักเก็บ')}</Box>
                          </TableCell>
                          <TableCell className={classes.hiddenLine} align="right">
                            {data.dam_storage}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            className={classes.hiddenLine}
                          >
                            <Box fontWeight={600} fontSize={12}>{t('น้ำไหลลง / ระบาย')}</Box>
                          </TableCell>
                          <TableCell className={classes.hiddenLine} align="right">
                            {data.dam_inflow}
                            {' '}
                            /
                            {' '}
                            {data.dam_released}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            className={classes.hiddenLine}
                          >
                            <Box fontWeight={400} fontSize={12} colSpan={3}>{`${t('หน่วย')} : (${t('ล้าน ลบ.ม.')})`}</Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell padding="none" align="center" colSpan={3} />
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.hiddenLine}>
                            <Box color="text.disabled" fontSize="8px">
                              {`${t('ข้อมูล')} : ${data.agency.agency_name.th}`}
                            </Box>
                            <Box color="text.disabled" fontSize="8px">
                              {`${t('ล่าสุด')} : ${data.dam_date}`}
                            </Box>
                          </TableCell>
                          <TableCell className={classes.hiddenLine} align="right">
                            <Button
                              title={t('แสดงกราฟ')}
                              style={{ padding: 0, minWidth: 18 }}
                              onClick={handleChart(data.dam.id)}
                            >
                              <FaChartLine className={classes.rightIcon} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                </TableBody>
              </Table>
            </div>
          </Popup>
          <Tooltip>
            <span>
              <strong>
                {t('อ่างเก็บน้ำ')}
                {data.dam.dam_name.th}
              </strong>
              <br />
              {`${t('ต.')}${data.geocode.tumbon_name.th} ${t('อ.')}${data.geocode.amphoe_name.th} `}
              <br />
              {`${t('จ.')} ${data.geocode.province_name.th}`}
            </span>
          </Tooltip>
        </Marker>
      );
    }

    return false;
  };

  const eachMediumDamData = (data, i) => {
    if (data.dam.dam_lat && data.dam.dam_long) {
      // marker position
      const position = [parseFloat(data.dam.dam_lat), parseFloat(data.dam.dam_long)];

      // marker icons
      const icon = setIcon(parseFloat(data.dam_storage_percent).toFixed(0), 'medium');

      // highlight icon in popup
      const highlightIcon = setHighlightIcon(parseFloat(data.dam_storage_percent).toFixed(0));
      const color = DamMediumColor(parseFloat(data.dam_storage_percent).toFixed(0));
      if (data.dam.dam_name.th === 'อ่างเก็บน้ำห้วยหินซะเเนน') {
        console.log('====================================');
        console.log(data);
        console.log('====================================');
      }
      return (
        <Marker
          id={data.id + data.dam.id + data.geocode.id}
          key={i}
          position={position}
          icon={icon}
          ref={bindMarker(data.id + data.dam.id + data.geocode.id)}
        >
          <Popup autoPan>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={2}>
                    <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                      {`${data.dam.dam_name.th.replace('อ่างเก็บน้ำ', '')} ${t('จ.')} ${data.geocode.province_name.th}`}
                    </Box>
                  </TableCell>
                </TableRow>
                {data.dam_date !== '1970-01-01'
                  && (
                    <>
                      <TableRow>
                        <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={2}>
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <Box>{highlightIcon}</Box>
                            <Box className={classes.statusValue}>
                              <span style={{ color: color.color.statusColor }}>
                                {`${parseFloat(data.dam_storage_percent).toFixed(0)}%`}
                              </span>
                            </Box>
                          </Box>
                          <Box mb={2} fontSize="14px" fontFamily="Kanit">
                            {t('ปริมาณน้ำกักเก็บปัจจุบัน (% รนก.)')}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell padding="none" align="center" colSpan={3} />
                      </TableRow>
                      <TableRow>
                        <TableCell
                          className={classes.hiddenLine}
                        >
                          <Box fontWeight={600} fontSize={12}>{t('ปริมาณน้ำกักเก็บ')}</Box>
                        </TableCell>
                        <TableCell className={classes.hiddenLine} align="right">
                          {data.dam_storage}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          className={classes.hiddenLine}
                        >
                          <Box fontWeight={600} fontSize={12}>{t('น้ำไหลลง / ระบาย')}</Box>
                        </TableCell>
                        <TableCell className={classes.hiddenLine} align="right">
                          {data.dam_inflow}
                          {' '}
                          /
                          {' '}
                          {data.dam_released}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          className={classes.hiddenLine}
                        >
                          <Box fontWeight={400} fontSize={12} colSpan={3}>{`${t('หน่วย')} : (${t('ล้าน ลบ.ม.')})`}</Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell padding="none" align="center" colSpan={3} />
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.hiddenLine}>
                          <Box fontSize="8px">
                            {`${t('ข้อมูล')}: ${data.agency.agency_name.th}`}
                          </Box>
                          <Box fontSize="8px">
                            {`${t('ล่าสุด')} : ${data.dam_date}`}
                          </Box>
                        </TableCell>
                        <TableCell className={classes.hiddenLine} align="right">
                          <Button
                            title={t('แสดงกราฟ')}
                            style={{ padding: 0, minWidth: 18 }}
                            onClick={handleChartMedium(data.dam.id)}
                          >
                            <FaChartLine className={classes.rightIcon} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
              </TableBody>
            </Table>
          </Popup>
          <Tooltip>
            <span>
              <strong>
                {data.dam.dam_name.th}
              </strong>
              <br />
              {`${t('ต.')}${data.geocode.tumbon_name.th} ${t('อ.')}${data.geocode.amphoe_name.th} `}
              <br />
              {`${t('จ.')} ${data.geocode.province_name.th}`}
            </span>
          </Tooltip>
        </Marker>
      );
    }

    return false;
  };

  const Modal = React.memo((p) => (
    <DamModal
      open={p.modalData.open}
      modalHeader={p.modalData.header}
      modalSubHeader={p.modalData.subheader}
      modalContent={p.modalData.content}
    />
  ));

  return (
    <>
      <Map
        id="map"
        center={currentArea.location ? currentArea.location : mapConfig.center}
        // zoom={currentArea.zoom ? currentArea.zoom : mapConfig.zoom}
        zoomSnap={0}
        zoomDelta={0.5}
        ref={refMap}
        style={{ height: 670, zIndex: 0 }}
        // bounds={bounds}
        boundsOptions={{ padding: [50, 50] }}
        gestureHandling
      >
        <Control position="bottomleft">
          <div className="info">
            <Typography variant="body1">
              <FontAwesomeIcon icon={faSquare} />
              {' '}
              {t('อ่างขนาดใหญ่')}
              <br />
              <FontAwesomeIcon icon={faCircle} />
              {' '}
              {t('อ่างขนาดกลาง')}
            </Typography>
          </div>
        </Control>

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          id="OpenStreetMap"
          attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>ESRI World Street Map</a> contributors"
        />
        {currentMode === 'province' && (
          <LayerProvince
            areaType={0}
            areaId={currentArea.code}
          />
        )}
        {currentMode === 'basin' && (
          <LayerBasin
            areaType={1}
            areaId={currentArea.code}
          />
        )}
        <LayersControl position="topright">
          <Overlay name={t('อ่างขนาดใหญ่')} checked="true">
            <LayerGroup ref={refDamLargeLayer}>
              {datasLargeDam
                .map(eachLargeDamData)}
            </LayerGroup>
          </Overlay>
          <Overlay name={t('อ่างขนาดกลาง')} checked="true">
            <LayerGroup ref={refDamMediumLayer}>
              {orderBy(datasMediumDam, ['dam_storage_percent'], ['asc'])
                .map(eachMediumDamData)}
            </LayerGroup>
          </Overlay>
          <Overlay name={t('เส้นแม่น้ำ')} checked>
            <LayerRiver />
          </Overlay>
          <Overlay name={t('ขอบเขตการปกครอง')}>
            <WmsBoundary />
          </Overlay>
          <Overlay name={t('ขอบเขตลุ่มน้ำ')}>
            <WmsBasin />
          </Overlay>
        </LayersControl>

        <FullscreenControl
          position="topleft"
          forcePseudoFullscreen
          content={LeafletFullScreenFix()}
        />
      </Map>

      <Modal modalData={modalData} />
    </>
  );
}

DamMap.propTypes = {
  datasLargeDam: PropTypes.instanceOf(Array).isRequired,
  datasMediumDam: PropTypes.instanceOf(Array).isRequired,
  handleSetMap: PropTypes.func.isRequired,
  markers: PropTypes.instanceOf(Array).isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
};
